import React from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Spinner,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ScrollShadow,
  Card,
  CardBody,
  Chip,
  addToast,
} from "@heroui/react";
import { TenantDocMeta } from "@webuild-ai/agent-platform/models/components";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { Filedrop } from "@/components/Filedrop/Filedrop";
import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";
import { PageTitle } from "@/components/UI/PageTitle";
import { siteConfig } from "@/config/site";
import { formatErrorForDisplay } from "@/utils/errorFormatter";
import { logger } from "@/telemetry";

export interface Tag {
  name: string;
}

export const DataHubFilesPage: React.FC = () => {
  const { platformClient, authenticatedFetch } = useWebuildAPIClient();
  const [files, setFiles] = React.useState<TenantDocMeta[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [deletingFileIds, setDeletingFileIds] = React.useState<Set<string>>(new Set());
  const [selectedSummary, setSelectedSummary] = React.useState<{
    fileId: string;
    summary: string;
  } | null>(null);
  const { t } = useTranslation();
  const { brand } = useBrand();

  const downloadFile = async (fileId: string, fileName: string) => {
    if (!platformClient || !authenticatedFetch) {
      addToast({
        title: t("Download failed"),
        description: t("Platform client or authentication not available"),
        color: "danger",
      });

      return;
    }

    try {
      const response = await platformClient.files.createTemporaryDownloadUrl({
        fileId: fileId,
      });

      if (!response || !response.temporary_url) {
        addToast({
          title: t("Download failed"),
          description: t("No temporary URL received from server"),
          color: "danger",
        });

        return;
      }

      // Build full URL for authenticated fetch
      const baseUrl = siteConfig().platformAPIURL;
      const tempUrl = response.temporary_url.startsWith("/")
        ? response.temporary_url
        : `/${response.temporary_url}`;
      const fullUrl = `${baseUrl}${tempUrl}`;

      // Use authenticated fetch to download the file
      const fetchResponse = await authenticatedFetch(fullUrl);

      if (!fetchResponse.ok) {
        throw new Error(`Download failed: ${fetchResponse.status} ${fetchResponse.statusText}`);
      }

      // Create blob and trigger download
      const blob = await fetchResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Use filename from response or fall back to file name
      const downloadFilename =
        response.filename || fileName.substring(fileName.indexOf("/") + 1) || fileName;

      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      URL.revokeObjectURL(blobUrl);

      addToast({
        title: t("Download started"),
        description: t("File download has started"),
        color: "success",
      });
    } catch (error) {
      logger.error("Error downloading file", error);
      addToast({
        title: t("Download failed"),
        description: formatErrorForDisplay(error),
        color: "danger",
      });
    }
  };

  const fetchFiles = React.useCallback(async () => {
    if (!platformClient) return;

    try {
      setIsLoading(true);
      const response = await platformClient.files.list({});

      setFiles(response || []);
    } catch (error) {
      logger.error("Error fetching files", error);
    } finally {
      setIsLoading(false);
    }
  }, [platformClient]);

  React.useEffect(() => {
    if (platformClient) {
      fetchFiles();
    }
  }, [platformClient, fetchFiles]);

  const renderSummary = (summary: string, fileId: string) => {
    const words = summary.split(" ");
    const isLong = words.length > 20;
    const displayText = isLong ? words.slice(0, 20).join(" ") + "..." : summary;

    return (
      <div className="space-y-2 break-words whitespace-pre-line bg-transparent">
        <div className="bg-transparent">
          <MarkdownWeBuild>{displayText}</MarkdownWeBuild>
        </div>
        {isLong && (
          <a
            className="text-xs text-primary hover:underline cursor-pointer"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setSelectedSummary({ fileId, summary });
            }}
          >
            {t("Show more")}
          </a>
        )}
      </div>
    );
  };

  const handleDelete = async (fileId: string) => {
    try {
      setDeletingFileIds((prev) => new Set(prev).add(fileId));
      await platformClient?.files.delete({
        filename: fileId,
      });
      await fetchFiles();
    } catch (error) {
      logger.error("Error deleting file", error);
    } finally {
      setDeletingFileIds((prev) => {
        const newSet = new Set(prev);

        newSet.delete(fileId);

        return newSet;
      });
    }
  };

  // Mobile card component for individual files
  const FileCard: React.FC<{ file: TenantDocMeta }> = ({ file }) => {
    const fileId = file.id ?? "";
    const fileTags = file.tags ?? [];
    const fileSummary = file.summary ?? "";
    const uploadedTimestamp = file.uploadedTimestamp ?? new Date().toISOString();

    return (
      <Card className="w-full" data-id={`file-card-${fileId}`}>
        <CardBody className="p-4">
          <div className="flex flex-col space-y-3">
            {/* File name and actions row */}
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <span
                  className="text-primary cursor-pointer hover:underline text-sm font-medium truncate block"
                  data-id={`download-file-${file.name}`}
                  role="button"
                  tabIndex={0}
                  title="Download file"
                  onClick={() => downloadFile(fileId, file.name)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      downloadFile(fileId, file.name);
                    }
                  }}
                >
                  {file.name.substring(file.name.indexOf("/") + 1) || file.name}
                </span>
              </div>
              <Button
                isIconOnly
                aria-label={`Delete ${file.name}`}
                color="danger"
                data-id={`delete-file-${fileId}`}
                isDisabled={deletingFileIds.has(fileId)}
                size="sm"
                onPress={() => handleDelete(fileId)}
              >
                <Icon className="text-base" icon="gravity-ui:trash-bin" />
              </Button>
            </div>

            {/* Summary */}
            <div className="text-sm">
              <div className="font-medium text-default-600 mb-1">{t("Summary")}</div>
              <div className="bg-transparent" data-id={`summary-container-${fileId}`}>
                {renderSummary(fileSummary, fileId)}
              </div>
            </div>

            {/* Tags */}
            {fileTags.filter((tag: string) => !tag.startsWith("username:")).length > 0 && (
              <div>
                <div className="font-medium text-default-600 mb-2 text-sm">{t("Tags")}</div>
                <div className="flex flex-wrap gap-1" data-id={`tags-container-${fileId}`}>
                  {fileTags
                    .filter((tag: string) => !tag.startsWith("username:"))
                    .map((tag: string, idx: number) => (
                      <Chip
                        key={idx}
                        className="text-xs"
                        color="primary"
                        data-id={`tag-${fileId}-${idx}`}
                        radius="sm"
                        size="sm"
                        variant="bordered"
                      >
                        {tag}
                      </Chip>
                    ))}
                </div>
              </div>
            )}

            {/* Upload info */}
            <div className="text-xs text-default-500" data-id={`uploaded-info-${fileId}`}>
              <div>
                {t("Uploaded by")}: {file.uploadedBy}
              </div>
              <div>{new Date(uploadedTimestamp).toLocaleDateString()}</div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <div className="p-4 max-w-full" data-id="datahub-files-page">
      <PageTitle
        centered
        dataId="datahub-title"
        icon="solar:database-linear"
        img={`/brands/${brand.prefix || "webuild"}/knowledge.svg`}
        subtitle="Pathway Index Description"
        title={`${brand.menuItems?.["Knowledge Hub"]?.name || "Knowledge Hub"} Files`}
      />
      <div className="rounded-lg shadow pb-4 mb-4" data-id="file-upload-container">
        <Filedrop data-id="file-upload-dropzone" onUploadComplete={fetchFiles} />
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-8">
          <Spinner size="sm" />
          <span className="text-sm">{t("Loading files...")}</span>
        </div>
      )}

      {/* No files state */}
      {!isLoading && files.length === 0 && (
        <div className="text-center py-8 text-default-500">{t("No files found")}</div>
      )}

      {/* Files content */}
      {!isLoading && files.length > 0 && (
        <>
          {/* Mobile layout - Cards */}
          <div className="flex flex-col gap-3 sm:hidden">
            {files.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>

          {/* Desktop layout - Table */}
          <div className="hidden sm:block">
            <Table
              aria-label={t("Files table")}
              classNames={{
                wrapper: "border border-divider rounded-lg w-full",
                th: "border-b border-divider",
                td: "border-b border-divider",
              }}
              data-id="files-table"
            >
              <TableHeader>
                <TableColumn className="w-48" data-id="column-filename">
                  {t("File Name")}
                </TableColumn>
                <TableColumn className="w-[60%]" data-id="column-summary">
                  {t("Summary")}
                </TableColumn>
                <TableColumn className="w-32" data-id="column-tags">
                  {t("Tags")}
                </TableColumn>
                <TableColumn className="w-36" data-id="column-uploaded-by">
                  {t("Uploaded By")}
                </TableColumn>
                <TableColumn className="w-12" data-id="column-actions">
                  {t("Actions")}
                </TableColumn>
              </TableHeader>
              <TableBody>
                {files.map((file) => {
                  const fileId = file.id ?? "";
                  const fileTags = file.tags ?? [];
                  const fileSummary = file.summary ?? "";
                  const uploadedTimestamp = file.uploadedTimestamp ?? new Date().toISOString();

                  return (
                    <TableRow key={fileId} data-id={`file-row-${fileId}`}>
                      <TableCell>
                        <span
                          className="text-primary cursor-pointer hover:underline"
                          data-id={`download-file-${file.name}`}
                          role="button"
                          tabIndex={0}
                          title="Download file"
                          onClick={() => downloadFile(fileId, file.name)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              downloadFile(fileId, file.name);
                            }
                          }}
                        >
                          {file.name.substring(file.name.indexOf("/") + 1) || file.name}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm break-words whitespace-pre-line bg-transparent">
                        <div
                          className="space-y-2 break-words whitespace-pre-line bg-transparent"
                          data-id={`summary-container-${fileId}`}
                        >
                          {renderSummary(fileSummary, fileId)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1" data-id={`tags-container-${fileId}`}>
                          {fileTags
                            .filter((tag: string) => !tag.startsWith("username:"))
                            .map((tag: string, idx: number) => (
                              <Button
                                key={idx}
                                className="text-xs"
                                color="primary"
                                data-id={`tag-${fileId}-${idx}`}
                                radius="sm"
                                size="sm"
                                variant="bordered"
                              >
                                {tag}
                              </Button>
                            ))}
                        </div>
                      </TableCell>
                      <TableCell data-id={`uploaded-info-${fileId}`}>
                        {file.uploadedBy}
                        <br />
                        {new Date(uploadedTimestamp).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          isIconOnly
                          aria-label={`Delete ${file.name}`}
                          color="danger"
                          data-id={`delete-file-${fileId}`}
                          isDisabled={deletingFileIds.has(fileId)}
                          onPress={() => handleDelete(fileId)}
                        >
                          <Icon className="text-lg" icon="gravity-ui:trash-bin" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Modal for full summary - made responsive */}
      <Modal
        classNames={{
          base: "full",
          body: "p-4 sm:p-6",
        }}
        isOpen={!!selectedSummary}
        size="full"
        onOpenChange={() => setSelectedSummary(null)}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 text-lg sm:text-xl">
                {t("File Summary")}
              </ModalHeader>
              <ModalBody>
                <ScrollShadow className="w-full max-h-[70vh] sm:max-h-[60vh]">
                  <div className="space-y-2 break-words whitespace-pre-line text-sm sm:text-base bg-transparent">
                    <div className="bg-transparent">
                      <MarkdownWeBuild>{selectedSummary?.summary || ""}</MarkdownWeBuild>
                    </div>
                  </div>
                </ScrollShadow>
              </ModalBody>
              <ModalFooter>
                <Button className="btn-brand-primary" onPress={onClose}>
                  {t("Close")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};
